import { useEffect, useRef, memo } from "react";
import { Product } from "~src/types/winningProducts";
import { Box, Grid, Typography } from "@mui/material";
import { useInView } from "react-intersection-observer";
import { useInfiniteBestSellingProducts } from "./useInfiniteWinningProducts";
import { BestSellingProductsFilters } from "~src/pages/winningProducts/Products";
import * as Sentry from "@sentry/react";
import { SkeletonCard } from "~src/components/SkeletonCard";
import { WinningProductTile } from "./WinningProductTile";

interface TrendingProductsListProps {
  filters: BestSellingProductsFilters | undefined;
  searchText: string | undefined;
  isCollapse: boolean;
  bestSellingProductsContext?: ReturnType<typeof useInfiniteBestSellingProducts>;
}

const PAGE_SIZE = 30;

const ProductItem = memo(({ product, isCollapse }: { product: Product; isCollapse: boolean }) => {
  return (
    <Grid size={{ xs: 12, sm: 6, md: isCollapse ? 3 : 4 }}>
      <WinningProductTile product={product} />
    </Grid>
  );
});

ProductItem.displayName = 'ProductItem';

export const TrendingProductsList = ({ 
  filters, 
  searchText, 
  isCollapse, 
  bestSellingProductsContext 
}: TrendingProductsListProps) => {
  const prevDataLength = useRef(0);
  const scrollPosition = useRef(0);
  const errorShown = useRef(false);
  
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1, // Trigger when 10% of the element is visible
    rootMargin: '200px', // Start loading when within 200px of the bottom
  });

  // Always call the hook unconditionally
  const ownProductContext = useInfiniteBestSellingProducts({
    pageSize: PAGE_SIZE,
    filters,
    searchText,
  });

  // Use the provided context if available, otherwise use our own
  const productContext = bestSellingProductsContext || ownProductContext;

  const { data, loadMore, loading, error, hasMore } = productContext;

  useEffect(() => {
    if (data.length !== prevDataLength.current) {
      prevDataLength.current = data.length;
    }
  }, [data, loading]);

  // Preload next page when approaching the end
  useEffect(() => {
    if (inView && !loading && hasMore && !errorShown.current) {
      loadMore();
    }
  }, [inView, loading, loadMore, hasMore]);

  // Save scroll position before re-render
  useEffect(() => {
    scrollPosition.current = window.scrollY;
  });

  // Restore scroll position after re-render
  useEffect(() => {
    if (data.length > prevDataLength.current) {
      window.scrollTo(0, scrollPosition.current);
    }
  }, [data.length]);

  // Handle error logging in an effect to avoid mutating refs during render
  useEffect(() => {
    if (error && !errorShown.current) {
      errorShown.current = true;
      Sentry.withScope((scope) => {
        scope.setTag("module", "TrendingProductsList");
        scope.setExtra("filters", filters);
        scope.setExtra("searchText", searchText);
        scope.setExtra("dataLength", data.length);
        Sentry.captureMessage("Error loading products");
      });
    }
  }, [error, filters, searchText, data.length]);

  if (error) {
    return (
      <Typography color="error">Error loading products. Please try again.</Typography>
    );
  }

  // Create a filter key string that changes when filters change
  const filterKey = `filter-${JSON.stringify(filters)}-${searchText || ""}`;
  
  return (
    <Grid container spacing={2} key={filterKey}>
      {data.map((product) => (
        <ProductItem key={`product-${product.product_id}`} product={product} isCollapse={isCollapse} />
      ))}
      
      {loading &&
        Array(4)
          .fill(null)
          .map((_, index) => (
            <Grid key={`loading-skeleton-${index}`} size={{ xs: 12, sm: 6, md: isCollapse ? 3 : 4 }}>
              <SkeletonCard />
            </Grid>
          ))}
          
      <div ref={loadMoreRef} style={{ height: '20px', marginTop: '20px' }} />
      
      {!loading && data.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography>No products found.</Typography>
        </Box>
      )}
      
      {!loading && !hasMore && data.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3 }}>
          <Typography>End of results</Typography>
        </Box>
      )}
    </Grid>
  );
};
